import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, Plus, ArrowUpDown, MessageSquare, Activity, Building2, Tag, 
  TrendingUp, TrendingDown, Minus, AlertTriangle, AlertCircle, CheckCircle2,
  Clock, Shield, Info, HelpCircle, Users, Percent
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { api } from '../utils/api';
import AddPatientModalEnhanced from './AddPatientModalEnhanced';
import InsuranceModal from './InsuranceModal';
import ConditionsModal from './ConditionsModal';

const INSURANCE_PROVIDERS = [
  "UnitedHealthcare",
  "Elevance Health (Anthem)",
  "Centene Corporation",
  "Humana Inc.",
  "CVS Health (Aetna)",
  "The Cigna Group",
  "Blue Cross Blue Shield",
  "Kaiser Permanente",
  "Molina Healthcare",
  "Oscar Health",
  "HealthMarkets",
  "Medical Mutual",
  "Mutual of Omaha",
  "CareSource",
  "Highmark",
  "Blue Shield of California",
  "Florida Blue",
  "Horizon Blue Cross Blue Shield",
  "Premera Blue Cross",
  "Tufts Health Plan"
];

// Configurable thresholds for Digital Twin Score color bands (TBD - configurable)
const DTS_THRESHOLDS = {
  green: 80,   // >= 80 is green
  yellow: 60,  // >= 60 is yellow
  // < 60 is red/orange
};

// Extracted components to avoid React render warnings

// Sort icon component
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
  return sortDirection === 'asc' ? <span>↑</span> : <span>↓</span>;
};

// Trend direction icon component
const TrendIcon = ({ direction }) => {
  if (direction === 'improving') return <TrendingUp className="w-3 h-3 text-green-600" />;
  if (direction === 'worsening') return <TrendingDown className="w-3 h-3 text-red-600" />;
  return <Minus className="w-3 h-3 text-gray-400" />;
};

// Metric tooltip component
const MetricTooltip = ({ title, children }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="inline-flex items-center text-gray-400 hover:text-gray-600 ml-1">
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        <p className="font-semibold mb-1">{title}</p>
        <p className="text-sm text-gray-600">{children}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const PatientList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [membersStats, setMembersStats] = useState(null);
  const [activePeriod, setActivePeriod] = useState(7); // Period for Active Rate calculation
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [insuranceFilter, setInsuranceFilter] = useState('all');
  const [checkInFilter, setCheckInFilter] = useState('all');
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [sortField, setSortField] = useState('risk');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedPatients, setSelectedPatients] = useState([]);
  
  // Modal states
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [selectedPatientForEdit, setSelectedPatientForEdit] = useState(null);

  useEffect(() => {
    loadData();
  }, [activePeriod]); // Reload when period changes

  useEffect(() => {
    filterAndSortPatients();
  }, [patients, searchQuery, riskFilter, statusFilter, insuranceFilter, checkInFilter, searchParams, sortField, sortDirection]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [membersRes, statsRes] = await Promise.all([
        api.getEnhancedMembers(),
        api.getMembersStats(activePeriod)
      ]);
      setPatients(membersRes.data);
      setMembersStats(statsRes.data);
    } catch (error) {
      // Fallback to regular patients endpoint if enhanced not available
      try {
        const response = await api.getPatients();
        setPatients(response.data);
      } catch (fallbackError) {
        toast.error('Failed to load members');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get unique insurance providers from patients for filter dropdown
  const getUniqueInsurers = () => {
    const insurers = new Set();
    patients.forEach(p => {
      if (p.insurance_provider) {
        insurers.add(p.insurance_provider);
      }
    });
    return Array.from(insurers).sort();
  };

  const filterAndSortPatients = () => {
    let filtered = [...patients];

    // Enhanced search - name, email, condition, ID, insurance
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.first_name.toLowerCase().includes(query) ||
          p.last_name.toLowerCase().includes(query) ||
          p.email.toLowerCase().includes(query) ||
          p.id.toLowerCase().includes(query) ||
          p.condition_tags.some((c) => c.toLowerCase().includes(query)) ||
          (p.insurance_provider && p.insurance_provider.toLowerCase().includes(query))
      );
    }

    // Apply risk filter (using risk_tier now)
    if (riskFilter !== 'all') {
      filtered = filtered.filter((p) => p.risk_tier === riskFilter);
    }

    // Apply insurance filter
    if (insuranceFilter !== 'all') {
      if (insuranceFilter === 'none') {
        filtered = filtered.filter((p) => !p.insurance_provider);
      } else {
        filtered = filtered.filter((p) => p.insurance_provider === insuranceFilter);
      }
    }

    // Apply check-in status filter
    if (checkInFilter !== 'all') {
      filtered = filtered.filter((p) => p.check_in_status === checkInFilter);
    }

    // Priority filter from URL
    if (searchParams.get('filter') === 'priority') {
      filtered = filtered.filter((p) => p.risk_tier === 'critical' || p.risk_tier === 'high');
    }

    // DTS filter from URL
    if (searchParams.get('dts') === 'low') {
      filtered = filtered.filter((p) => p.digital_twin_score < 70);
    }

    // Mood filter from URL
    if (searchParams.get('mood') === 'low') {
      filtered = filtered.filter((p) => p.mood_score < 6);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }

    // Apply sorting (risk-first is default)
    if (sortField === 'risk') {
      // Urgency-first: Critical > High > Medium > Low
      // Tie-breakers: worsening trend, lower adherence, escalation
      const riskOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const trendOrder = { worsening: 0, stable: 1, improving: 2 };
      
      filtered.sort((a, b) => {
        const riskA = riskOrder[a.risk_tier] ?? 2;
        const riskB = riskOrder[b.risk_tier] ?? 2;
        if (riskA !== riskB) return sortDirection === 'asc' ? riskA - riskB : riskB - riskA;
        
        // Tie-breaker 1: Trend (worsening first)
        const trendA = trendOrder[a.trend_direction] ?? 1;
        const trendB = trendOrder[b.trend_direction] ?? 1;
        if (trendA !== trendB) return trendA - trendB;
        
        // Tie-breaker 2: Lower adherence first
        return (a.adherence_rate || 1) - (b.adherence_rate || 1);
      });
    } else {
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        switch(sortField) {
          case 'name':
            aVal = `${a.first_name} ${a.last_name}`;
            bVal = `${b.first_name} ${b.last_name}`;
            break;
          case 'dts':
            aVal = a.digital_twin_score;
            bVal = b.digital_twin_score;
            break;
          case 'mood':
            aVal = a.mood_score;
            bVal = b.mood_score;
            break;
          case 'last_sync':
            aVal = new Date(a.last_sync).getTime();
            bVal = new Date(b.last_sync).getTime();
            break;
          case 'insurance':
            aVal = a.insurance_provider || '';
            bVal = b.insurance_provider || '';
            break;
          case 'adherence':
            aVal = a.adherence_rate || 0;
            bVal = b.adherence_rate || 0;
            break;
          default:
            aVal = a[sortField];
            bVal = b[sortField];
        }

        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    setFilteredPatients(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'risk' ? 'asc' : 'desc');
    }
  };

  const handleSelectPatient = (patientId) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPatients.length === filteredPatients.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(filteredPatients.map(p => p.id));
    }
  };

  const handleBatchMessage = () => {
    toast.success(`Sending message to ${selectedPatients.length} member(s)`);
    setSelectedPatients([]);
  };

  const handleBatchCheckin = () => {
    toast.success(`Check-in request sent to ${selectedPatients.length} member(s)`);
    setSelectedPatients([]);
  };

  const handleEditInsurance = (e, patient) => {
    e.stopPropagation();
    setSelectedPatientForEdit(patient);
    setShowInsuranceModal(true);
  };

  const handleEditConditions = (e, patient) => {
    e.stopPropagation();
    setSelectedPatientForEdit(patient);
    setShowConditionsModal(true);
  };

  // Risk tier badge styling (non-diagnostic)
  const getRiskTierBadge = (tier) => {
    const styles = {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-gray-900',
      low: 'bg-green-500 text-white'
    };
    const labels = {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low'
    };
    return { style: styles[tier] || styles.medium, label: labels[tier] || 'Medium' };
  };

  // Digital Twin Score color based on configurable thresholds
  const getDTSColor = (score) => {
    if (score >= DTS_THRESHOLDS.green) return 'text-green-600';
    if (score >= DTS_THRESHOLDS.yellow) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getDTSBgColor = (score) => {
    if (score >= DTS_THRESHOLDS.green) return 'bg-green-100 border-green-200';
    if (score >= DTS_THRESHOLDS.yellow) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-100 border-red-200';
  };

  // Check-in status badge
  const getCheckInBadge = (status) => {
    const configs = {
      completed: { icon: CheckCircle2, color: 'bg-green-100 text-green-800 border-green-200', label: 'Completed' },
      scheduled: { icon: Clock, color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Scheduled' },
      due: { icon: AlertCircle, color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Due' },
      overdue: { icon: AlertTriangle, color: 'bg-red-100 text-red-800 border-red-200', label: 'Overdue' },
      skipped: { icon: Minus, color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Skipped' }
    };
    return configs[status] || configs.scheduled;
  };

  // Confidence/completeness indicator
  const getConfidenceIndicator = (confidence, completeness) => {
    const confPct = Math.round(confidence * 100);
    let color = 'text-green-600';
    if (confPct < 70) color = 'text-red-600';
    else if (confPct < 85) color = 'text-yellow-600';
    
    return { percentage: confPct, color, completeness };
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const truncateText = (text, maxLength) => {
    if (!text) return null;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '…';
  };

  // Render conditions with truncation
  const renderConditions = (conditions, patient, maxVisible = 2) => {
    if (!conditions || conditions.length === 0) {
      return (
        <button
          onClick={(e) => handleEditConditions(e, patient)}
          className="inline-flex items-center text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-2 py-1 rounded-full transition-colors"
          aria-label="Add condition"
          data-testid={`add-conditions-${patient.id}`}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add
        </button>
      );
    }

    const visibleConditions = conditions.slice(0, maxVisible);
    const hiddenCount = conditions.length - maxVisible;

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visibleConditions.map((condition, idx) => (
          <span
            key={idx}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200"
            title={condition}
          >
            {truncateText(condition, 12)}
          </span>
        ))}
        {hiddenCount > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200 cursor-default">
                  +{hiddenCount}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="font-semibold mb-1">All Conditions:</p>
                {conditions.map((c, i) => (
                  <span key={i} className="block text-sm">{c}</span>
                ))}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <button
          onClick={(e) => handleEditConditions(e, patient)}
          className="text-gray-400 hover:text-purple-600 p-0.5 rounded transition-colors"
          aria-label="Edit conditions"
          data-testid={`edit-conditions-${patient.id}`}
        >
          <Tag className="w-3 h-3" />
        </button>
      </div>
    );
  };

  // Render insurance with truncation
  const renderInsurance = (insurance, patient) => {
    if (!insurance) {
      return (
        <button
          onClick={(e) => handleEditInsurance(e, patient)}
          className="inline-flex items-center text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-50 px-2 py-1 rounded-full transition-colors"
          aria-label="Add insurance provider"
          data-testid={`add-insurance-${patient.id}`}
        >
          <Plus className="w-3 h-3 mr-1" />
          Add
        </button>
      );
    }

    const truncated = truncateText(insurance, 14);
    const needsTooltip = insurance.length > 14;

    return (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span 
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200 cursor-default"
                data-testid={`insurance-cell-${patient.id}`}
              >
                <Building2 className="w-3 h-3 mr-1" />
                {truncated}
              </span>
            </TooltipTrigger>
            {needsTooltip && (
              <TooltipContent side="top">
                {insurance}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        <button
          onClick={(e) => handleEditInsurance(e, patient)}
          className="text-gray-400 hover:text-blue-600 p-0.5 rounded transition-colors"
          aria-label="Edit insurance provider"
          data-testid={`edit-insurance-${patient.id}`}
        >
          <Building2 className="w-3 h-3" />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-6 fade-in" data-testid="members-page">
      {/* Header with Active Members Stats */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="member-list-title">
            All Members
          </h1>
          <p className="text-gray-600 mt-1">
            {filteredPatients.length} member{filteredPatients.length !== 1 ? 's' : ''} found
            {sortField === 'risk' && sortDirection === 'asc' && <span className="ml-2 text-xs text-purple-600 font-medium">Sorted: High-risk first</span>}
          </p>
        </div>
        <Button
          onClick={() => setShowAddPatient(true)}
          className="rounded-full self-start lg:self-auto"
          style={{ background: 'linear-gradient(135deg, #8F2FDF 0%, #CE08B1 100%)' }}
          data-testid="add-patient-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Member
        </Button>
      </div>

      {/* Active Members & Active Rate KPI Cards */}
      {membersStats && (
        <div className="space-y-3">
          {/* Period Selector */}
          <div className="flex items-center justify-end gap-2">
            <span className="text-sm text-gray-500">Reporting Period:</span>
            <Select value={String(activePeriod)} onValueChange={(val) => setActivePeriod(Number(val))}>
              <SelectTrigger className="w-32 h-8 text-sm" data-testid="period-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* KPI Cards Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="members-stats-row">
            <Card className="col-span-1" data-testid="stat-active-members">
              <CardContent className="pt-3 pb-2 px-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center">
                      Active Members
                      <MetricTooltip title="Active Members">
                        Count of unique members with at least 1 meaningful platform interaction during the selected reporting period.
                      </MetricTooltip>
                    </p>
                    <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {membersStats.active_members}
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Users className="w-4 h-4 text-purple-700" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">of {membersStats.total_enrolled} enrolled</p>
              </CardContent>
            </Card>

            <Card className="col-span-1" data-testid="stat-active-rate">
              <CardContent className="pt-3 pb-2 px-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center">
                      Active Rate
                      <MetricTooltip title="Active Rate">
                        (Active members during period ÷ Total enrolled members during period) × 100. Measures platform engagement health.
                      </MetricTooltip>
                    </p>
                    <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {membersStats.active_rate}%
                    </p>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <Percent className="w-4 h-4 text-green-700" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Last {membersStats.period_days} days</p>
              </CardContent>
            </Card>

            <Card className="col-span-1" data-testid="stat-critical">
            <CardContent className="pt-3 pb-2 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Critical</p>
                  <p className="text-xl font-bold mt-0.5 text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {membersStats.critical_count}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-1" data-testid="stat-high">
            <CardContent className="pt-3 pb-2 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">High Risk</p>
                  <p className="text-xl font-bold mt-0.5 text-orange-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {membersStats.high_count}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-orange-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-1" data-testid="stat-escalated">
            <CardContent className="pt-3 pb-2 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center">
                    Escalated
                    <MetricTooltip title="Escalation">
                      Transition from routine monitoring to active follow-up due to predefined triggers: repeated missed check-ins, persistent non-adherence, worsening indicators, or high-risk threshold crossing.
                    </MetricTooltip>
                  </p>
                  <p className="text-xl font-bold mt-0.5 text-purple-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {membersStats.escalated_count}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-1" data-testid="stat-overdue">
            <CardContent className="pt-3 pb-2 px-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center">
                    Overdue
                    <MetricTooltip title="Overdue Check-In">
                      Member missed their scheduled check-in after the completion window plus grace period has passed. Requires follow-up.
                    </MetricTooltip>
                  </p>
                  <p className="text-xl font-bold mt-0.5 text-amber-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {membersStats.overdue_checkins}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      {/* Filters and Batch Actions */}
      <Card className="p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name, email, condition, insurance..."
                aria-label="Search members"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger data-testid="risk-filter" aria-label="Filter by risk tier">
                <SelectValue placeholder="Risk Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Tiers</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select value={checkInFilter} onValueChange={setCheckInFilter}>
              <SelectTrigger data-testid="checkin-filter" aria-label="Filter by check-in status">
                <SelectValue placeholder="Check-in Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Check-ins</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="due">Due</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={insuranceFilter} onValueChange={setInsuranceFilter}>
              <SelectTrigger data-testid="insurance-filter" aria-label="Filter by insurance provider">
                <SelectValue placeholder="Insurance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Insurance</SelectItem>
                <SelectItem value="none">No Insurance</SelectItem>
                {getUniqueInsurers().map(insurer => (
                  <SelectItem key={insurer} value={insurer}>{truncateText(insurer, 20)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Batch Actions Bar */}
          {selectedPatients.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200" role="region" aria-label="Batch actions">
              <span className="text-sm font-medium text-purple-900">
                {selectedPatients.length} member{selectedPatients.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBatchMessage}
                  data-testid="batch-message-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Send Message
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBatchCheckin}
                  data-testid="batch-checkin-btn"
                >
                  <Activity className="w-4 h-4 mr-1" />
                  Request Check-In
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedPatients([])}
                  data-testid="clear-selection-btn"
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Member Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton h-20"></div>
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full" role="grid" aria-label="Members table" data-testid="members-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left w-10" scope="col">
                    <input
                      type="checkbox"
                      checked={selectedPatients.length === filteredPatients.length && filteredPatients.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                      aria-label="Select all members"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                    scope="col"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Name</span>
                      <SortIcon field="name" sortField={sortField} sortDirection={sortDirection} />
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('risk')}
                    scope="col"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Risk Tier</span>
                      <SortIcon field="risk" sortField={sortField} sortDirection={sortDirection} />
                      <MetricTooltip title="Risk Tier (Non-Diagnostic)">
                        Represents deviation of member&apos;s actual state from expected Digital Twin state. Not a medical diagnosis. Critical = urgent attention needed; Low = within expected parameters.
                      </MetricTooltip>
                    </div>
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('dts')}
                    scope="col"
                  >
                    <div className="flex items-center space-x-1">
                      <span>DTS</span>
                      <SortIcon field="dts" sortField={sortField} sortDirection={sortDirection} />
                      <MetricTooltip title="Digital Twin Score">
                        Composite wellness score based on biometrics, behavior, and context. Color bands indicate deviation from expected state (thresholds configurable).
                      </MetricTooltip>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" scope="col">
                    <div className="flex items-center">
                      <span>Primary Reason</span>
                      <MetricTooltip title="Primary Reason Flagged">
                        The highest defensible driver for this member&apos;s current risk state. Describes deviation from expected state, not a medical diagnosis.
                      </MetricTooltip>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" scope="col">
                    <div className="flex items-center">
                      <span>Check-in</span>
                      <MetricTooltip title="Check-in Status">
                        Scheduled = not yet due; Due = active completion window; Completed = on time; Overdue = missed after grace period.
                      </MetricTooltip>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" scope="col">
                    Conditions
                  </th>
                  <th 
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('last_sync')}
                    scope="col"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Sync</span>
                      <SortIcon field="last_sync" sortField={sortField} sortDirection={sortDirection} />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" scope="col">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPatients.map((patient) => {
                  const riskBadge = getRiskTierBadge(patient.risk_tier);
                  const checkInBadge = getCheckInBadge(patient.check_in_status);
                  const CheckInIcon = checkInBadge.icon;
                  const confidenceInfo = getConfidenceIndicator(patient.confidence_score || 0.85, patient.data_completeness_status);
                  
                  return (
                    <tr
                      key={patient.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedPatients.includes(patient.id) ? 'bg-purple-50' : ''} ${patient.escalation_status === 'active' ? 'border-l-4 border-l-red-500' : ''}`}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <td className="px-3 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPatients.includes(patient.id)}
                          onChange={() => handleSelectPatient(patient.id)}
                          className="rounded border-gray-300"
                          aria-label={`Select ${patient.first_name} ${patient.last_name}`}
                          data-testid={`checkbox-${patient.id}`}
                        />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" onClick={() => navigate(`/patients/${patient.id}`)}>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-1">
                              {patient.first_name} {patient.last_name}
                              {patient.escalation_status === 'active' && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Shield className="w-3.5 h-3.5 text-red-600" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="font-semibold">Escalated</p>
                                      <p className="text-sm">{patient.escalation_triggers?.join(', ') || 'Active follow-up required'}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{patient.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" onClick={() => navigate(`/patients/${patient.id}`)}>
                        <div className="flex items-center gap-1.5">
                          <Badge className={`${riskBadge.style} text-xs px-2 py-0.5`} data-testid={`risk-tier-${patient.id}`}>
                            {riskBadge.label}
                          </Badge>
                          <TrendIcon direction={patient.trend_direction} />
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" onClick={() => navigate(`/patients/${patient.id}`)}>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${getDTSBgColor(patient.digital_twin_score)}`}>
                                <span className={`font-semibold ${getDTSColor(patient.digital_twin_score)}`} data-testid={`dts-${patient.id}`}>
                                  {patient.digital_twin_score}
                                </span>
                                {confidenceInfo.completeness !== 'complete' && (
                                  <Info className="w-3 h-3 text-gray-400" />
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>Confidence: <span className={confidenceInfo.color}>{confidenceInfo.percentage}%</span></p>
                              <p className="text-xs text-gray-500">Data: {confidenceInfo.completeness}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="px-3 py-3" onClick={() => navigate(`/patients/${patient.id}`)}>
                        <span className="text-sm text-gray-700" data-testid={`reason-${patient.id}`}>
                          {truncateText(patient.primary_reason_flag, 28) || 'Distance from expected state'}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap" onClick={() => navigate(`/patients/${patient.id}`)}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${checkInBadge.color}`} data-testid={`checkin-${patient.id}`}>
                          <CheckInIcon className="w-3 h-3" />
                          {checkInBadge.label}
                        </span>
                      </td>
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        {renderConditions(patient.condition_tags, patient)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600" onClick={() => navigate(`/patients/${patient.id}`)}>
                        {getTimeAgo(patient.last_sync)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/messages');
                            }}
                            data-testid={`message-${patient.id}`}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.success('Check-in request sent!');
                            }}
                            data-testid={`checkin-action-${patient.id}`}
                          >
                            <Activity className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/patients/${patient.id}`);
                            }}
                            data-testid={`view-patient-${patient.id}`}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredPatients.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No members found matching your criteria</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Modals */}
      <AddPatientModalEnhanced
        open={showAddPatient}
        onClose={() => setShowAddPatient(false)}
        onSuccess={loadData}
      />

      <InsuranceModal
        open={showInsuranceModal}
        onClose={() => {
          setShowInsuranceModal(false);
          setSelectedPatientForEdit(null);
        }}
        patient={selectedPatientForEdit}
        onSuccess={loadData}
      />

      <ConditionsModal
        open={showConditionsModal}
        onClose={() => {
          setShowConditionsModal(false);
          setSelectedPatientForEdit(null);
        }}
        patient={selectedPatientForEdit}
        onSuccess={loadData}
      />
    </div>
  );
};

export default PatientList;
